/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Container, MenuItem, Snackbar, TextField,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ROUTES from '../../../../Configs/routes';
import Elements from '../../../Elements';
import { sellItemToMarket } from '../../../../Redux/Models/market';
import { getUserData } from '../../../../Utils/storages';

export default function SellProduct({
  classes, error, message, forSale, isLoading,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationSent = useLocation();
  const { t } = useTranslation();

  const user = getUserData();

  const [selectedItem, setSelectedItem] = useState({});
  const [saleFields, setSaleField] = useState({
    sale_type: '-',
    starting_price: '',
    bid_duration: '-',
    description: '',
  });
  const [openPop, setOpenPop] = useState(false);

  const imageUrl = selectedItem.item?.photo ?? selectedItem.box?.photo;
  const productId = selectedItem.item?.id ?? selectedItem.box?.id;

  const handleChangeSaleField = (e) => {
    setSaleField({ ...saleFields, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (locationSent.state) {
      setSelectedItem(locationSent.state);
    } else {
      navigate(ROUTES.market());
    }
  }, [locationSent.state]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  const wantToSell = () => {
    const hasPrice = Number(saleFields.starting_price) > 0;
    if ((saleFields.sale_type === 'Fixed Price' && hasPrice && saleFields.description)
        || (saleFields.sale_type === 'Bid Price' && hasPrice && saleFields.description && saleFields.bid_duration)) {
      setOpenPop(true);
    } else {
      setSnackbar({
        open: true,
        message: 'Please complete the form!',
        severity: 'error',
      });
    }
  };

  const sellItem = () => {
    const {
      name,
      volume,
      notes,
    } = selectedItem;

    const body = {
      item: {
        name,
        description: saleFields.description,
        volume,
        sale_type: saleFields.sale_type,
        starting_price: Number(saleFields.starting_price),
        notes,
        type: selectedItem.type,
      },
    };

    if (saleFields.sale_type === 'Bid Price') {
      const end_date = new Date();
      end_date.setDate(end_date.getDate() + Number(saleFields.bid_duration));
      body.item.end_date = end_date.toISOString();
    }
    dispatch(sellItemToMarket(user.id, productId, body));
  };

  useEffect(() => {
    if (forSale) {
      navigate(`${ROUTES.market()}/search/${productId}?type=${selectedItem.type.toLowerCase()}`);
    }
  }, [forSale]);

  return (
    <Elements.PageBase navigate={navigate} noNav>
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={snackbar.open} autoHideDuration={10000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity || 'success'}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <div className={classes.root}>
        <Elements.Navigator title={t('sale.title')} />

        <Container sx={{ mt: 3 }}>
          <h4>{t('sale.selected')}</h4>
          <Box className="selected-item rounded flex gap-8 mt-3">
            <img
              src={imageUrl}
              alt="item"
              width="100"
              height="100"
              className="rounded contain"
              onClick={() => window.open(imageUrl, '_blank')}
            />
            <div>
              <div className="s-14 two-line weight-600">{selectedItem.name}</div>
              <div className="s-15 mt-2">
                {t('sale.type')}
                :
                {' '}
                {selectedItem.type || '-'}
              </div>
              <div className="s-15 mt-2">
                {t('sale.condition')}
                :
                {' '}
                {selectedItem.condition?.name || '-'}
              </div>
              <div className="s-15 mt-1">
                {t('sale.category')}
                :
                {' '}
                {selectedItem.category?.name || '-'}
              </div>
            </div>
          </Box>

          <h4 className="mt-5">{t('sale.market')}</h4>
          <div className="mt-3">
            <label>{t('sale.field1')}</label>
            <TextField fullWidth select value={saleFields.sale_type} name="sale_type" onChange={handleChangeSaleField}>
              <MenuItem value="-" disabled>{t('sale.chooseType')}</MenuItem>
              <MenuItem value="Fixed Price">{t('sale.fixed')}</MenuItem>
              <MenuItem value="Bid Price">{t('sale.bid')}</MenuItem>
            </TextField>
          </div>

          <div className="mt-3" hidden={saleFields.sale_type !== 'Fixed Price'}>
            <label>{t('sale.field2')}</label>
            <TextField
              fullWidth
              placeholder="123"
              InputProps={{
                startAdornment: (<strong className="s-16">₣</strong>),
              }}
              value={saleFields.starting_price}
              name="starting_price"
              onChange={handleChangeSaleField}
            />
          </div>

          <div className="mt-3" hidden={saleFields.sale_type !== 'Bid Price'}>
            <label>{t('sale.field3')}</label>
            <TextField
              fullWidth
              placeholder="123"
              InputProps={{
                startAdornment: (<strong className="s-16">₣</strong>),
              }}
              value={saleFields.starting_price}
              name="starting_price"
              onChange={handleChangeSaleField}
            />
          </div>

          <div className="mt-3" hidden={saleFields.sale_type !== 'Bid Price'}>
            <label>{t('sale.field4')}</label>
            <TextField fullWidth select value={saleFields.bid_duration} name="bid_duration" onChange={handleChangeSaleField}>
              <MenuItem value="-" disabled>{t('sale.chooseDuration')}</MenuItem>
              <MenuItem value={3}>3 days</MenuItem>
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={14}>2 weeks</MenuItem>
              <MenuItem value={21}>3 weeks</MenuItem>
              <MenuItem value={30}>1 month</MenuItem>
            </TextField>
          </div>

          <div className="mt-3">
            <label>{t('sale.field5')}</label>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={12}
              value={saleFields.description}
              name="description"
              onChange={handleChangeSaleField}
            />
          </div>
        </Container>

        <div className="floating-action">
          <Container>
            <Button fullWidth size="large" variant="contained" className="btn-contained" onClick={wantToSell}>
              {t('sale.sell')}
            </Button>
          </Container>
        </div>
      </div>

      <Elements.ConfirmationModal
        open={openPop}
        desc={(
          <div>
            {t('sale.confirm')}
          </div>
        )}
        handleClose={() => setOpenPop(false)}
        handleAgree={sellItem}
        confirmText="Sell"
        isLoading={isLoading}
      />
    </Elements.PageBase>
  );
}
